import { useContext, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { LockedAutocomplete, type AutocompleteOption } from "@/components/LockedAutocomplete";
import { NewProjectButton } from "@/components/NewProjectButton";
import { ProjectOverviewButton } from "@/components/ProjectOverviewButton";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Checkbox, TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { ProjectOverviewContext } from "@/projectOverview/projectOverviewContextValue";
import {
  PROJECT_OVERVIEW_NESTED_OVERLAY_Z,
  PROJECT_OVERVIEW_OVERLAY_Z,
} from "@/projectOverview/projectOverviewLayers";
import {
  AUFTRAG_AUTOCOMPLETE_EMPTY,
  AUFTRAG_AUTOCOMPLETE_PLACEHOLDER,
  auftragLookupToOption,
  loadAuftragAutocompleteOptions,
} from "@/lib/auftragAutocomplete";
import { fetchAuftragBookedHoursDetail, formatHours } from "@/lib/bookedHours";
import {
  activateHeroDokument,
  convertDokument,
  createDokument,
  DOKUMENT_STATUS_LABELS,
  DOKUMENT_TYP_LABELS,
  dokumentDisplayNumber,
  dokumentDisplayTitle,
  getAuftragQuelle,
  getDokument,
  getDokumentTypLabel,
  getGeschaeftspartnerQuelle,
  getKundenStundenArtikel,
  geschaeftspartnerQuelleToUpdate,
  isHeroCustomerDocumentListItem,
  isHeroOriginDokument,
  listDokumente,
  updateDokument,
  type DokumentCreateInput,
  type DokumentDetail,
  type DokumentListItem,
  type DokumentPosition,
  type DokumentTyp,
  type GeschaeftspartnerQuelle,
} from "@/lib/dokumente";
import type { Geschaeftspartner } from "@/lib/kontakte";
import { loadAppKundeOptions } from "@/pages/dokumente/kundeAutocomplete";
import type { AuftragLookup } from "@/lib/material";
import type { CreatedOrder } from "@/lib/projects";
import { getOrder } from "@/lib/projectOverview";
import {
  ExistingLieferscheinAlert,
  ExistingLieferscheinDialog,
} from "@/pages/dokumente/ExistingLieferscheinDialog";
import {
  loadExistingLieferscheine,
  type ExistingLieferscheinHint,
} from "@/pages/dokumente/existingLieferscheinHint";
import { DocumentSourcePickerDialog } from "@/pages/dokumente/DocumentSourcePickerDialog";
import { useAuftragsbestaetigungFromAngebot } from "@/pages/dokumente/useAuftragsbestaetigungFromAngebot";
import {
  createGebuchteStundenPosition,
  toApiPosition,
  type NachweisStundenArtikelDefaults,
} from "@/pages/dokumente/documentPositionModel";
import { stripGdRichTextMarkup } from "@/lib/gdRichText";
import { ensureDokumentStandardtexte, loadGdStandardtexteMap } from "@/lib/gdStandardtexte";

/** Reihenfolge im Dialog „Neues Dokument“. */
const TYPEN: DokumentTyp[] = ["angebot", "auftragsbestaetigung", "lieferschein", "rechnung"];
const DRAFT_CONFLICT_TYPEN: DokumentTyp[] = ["angebot", "rechnung"];

type RechnungCreateMode =
  | "standard"
  | "abschlag_prozent"
  | "abschlag_festbetrag"
  | "schlussrechnung";

const RECHNUNG_CREATE_MODES: Array<{ id: RechnungCreateMode; label: string; hint: string }> = [
  {
    id: "standard",
    label: "Standard",
    hint: "Positionen unverändert übernehmen (oder leer anlegen).",
  },
  {
    id: "abschlag_prozent",
    label: "Abschlag %",
    hint: "Angebotspositionen anteilig mit Kennzeichnung „X % Abschlag“.",
  },
  {
    id: "abschlag_festbetrag",
    label: "Abschlag Festbetrag",
    hint: "Festen Nettobetrag anteilig auf die Angebotspositionen verteilen.",
  },
  {
    id: "schlussrechnung",
    label: "Schlussrechnung",
    hint: "Volles Angebot, optional Stunden, Abzug vorheriger Teilrechnungen.",
  },
];

/**
 * Vorgänger-Typen, aus denen der gewählte Typ per Umwandlung erstellt werden kann.
 * Angebote haben keine Vorgänger-Vorschläge.
 */
const CREATE_FROM_SOURCES: Partial<Record<DokumentTyp, DokumentTyp[]>> = {
  // Kopie aus bestehendem (auch Hero-)Angebot.
  angebot: ["angebot"],
  auftragsbestaetigung: ["angebot"],
  lieferschein: ["auftragsbestaetigung", "angebot"],
  rechnung: ["lieferschein", "auftragsbestaetigung", "angebot"],
};

type InhaltMode = "leer" | "dokument";

function orderToAuftragLookup(order: {
  id: number;
  auftragsnummer: string;
  jahr?: number | null;
  kunde?: string | null;
  ort?: string | null;
  ort_zusatz?: string | null;
  beschreibung?: string | null;
}): AuftragLookup {
  return {
    id: order.id,
    auftragsnummer: order.auftragsnummer,
    jahr: order.jahr ?? null,
    kunde: order.kunde ?? null,
    ort: order.ort ?? null,
    ort_zusatz: order.ort_zusatz ?? null,
    beschreibung: order.beschreibung ?? null,
  };
}

function isConvertibleSource(doc: DokumentListItem): boolean {
  // Virtuelle Hero-Listenzeilen (negative id) und aktivierte Hero-GDs sind erlaubt.
  if (isHeroCustomerDocumentListItem(doc)) {
    if (doc.status === "entwurf" || doc.status === "storniert" || doc.status === "abgelehnt") {
      return false;
    }
    return doc.hero_document_id != null && Number(doc.hero_document_id) > 0;
  }
  if (!Number.isFinite(doc.id) || doc.id <= 0) return false;
  if (doc.status === "entwurf" || doc.status === "storniert" || doc.status === "abgelehnt") {
    return false;
  }
  if (doc.has_newer_revision) return false;
  return true;
}

/** Aktiviert Hero-Quellen bei Bedarf und liefert eine echte App-GD-Id. */
async function resolveSourceDokumentId(source: DokumentListItem): Promise<number> {
  if (isHeroCustomerDocumentListItem(source)) {
    const heroId = Number(source.hero_document_id);
    if (!Number.isFinite(heroId) || heroId <= 0) {
      throw new Error("Hero-Dokument ohne gültige ID.");
    }
    const activated = await activateHeroDokument(heroId);
    return activated.id;
  }
  if (!Number.isFinite(source.id) || source.id <= 0) {
    throw new Error("Ungültige Quelldokument-ID.");
  }
  return source.id;
}

export interface DocumentCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (docId: number) => void;
  initialAuftragId?: number | null;
  initialTyp?: DokumentTyp;
  initialQuellDokumentId?: number | null;
}

export function DocumentCreateDialog({
  open,
  onClose,
  onCreated,
  initialAuftragId = null,
  initialTyp = "angebot",
  initialQuellDokumentId = null,
}: DocumentCreateDialogProps) {
  const { open: openAuftragsbestaetigungFromAngebot } = useAuftragsbestaetigungFromAngebot();
  const overviewOpen = useContext(ProjectOverviewContext)?.isOpen ?? false;
  const dialogZIndexClass = overviewOpen ? PROJECT_OVERVIEW_OVERLAY_Z : "z-50";
  const conflictZIndexClass = overviewOpen ? PROJECT_OVERVIEW_NESTED_OVERLAY_Z : "z-[70]";
  const [project, setProject] = useState<AutocompleteOption<AuftragLookup> | null>(null);
  const [projectLoading, setProjectLoading] = useState(false);
  const [typ, setTyp] = useState<DokumentTyp>(
    TYPEN.includes(initialTyp) ? initialTyp : "angebot",
  );
  const [inhaltMode, setInhaltMode] = useState<InhaltMode>(
    initialQuellDokumentId ? "dokument" : "leer",
  );
  const [selectedSource, setSelectedSource] = useState<DokumentListItem | null>(null);
  const [sourcePickerOpen, setSourcePickerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gebuchteStundenTotal, setGebuchteStundenTotal] = useState<number | null>(null);
  const [gebuchteStundenLoading, setGebuchteStundenLoading] = useState(false);
  const [includeGebuchteStunden, setIncludeGebuchteStunden] = useState(true);
  const [draftConflictOpen, setDraftConflictOpen] = useState(false);
  const [existingDrafts, setExistingDrafts] = useState<DokumentListItem[]>([]);
  const [lieferscheinConflictOpen, setLieferscheinConflictOpen] = useState(false);
  const [existingLieferscheine, setExistingLieferscheine] = useState<ExistingLieferscheinHint[]>([]);
  const [pendingLieferscheinSource, setPendingLieferscheinSource] = useState<DokumentListItem | null>(
    null,
  );
  const [sourceDocs, setSourceDocs] = useState<DokumentListItem[]>([]);
  const [sourceDocsLoading, setSourceDocsLoading] = useState(false);
  const [convertingFromId, setConvertingFromId] = useState<number | null>(null);
  const [rechnungMode, setRechnungMode] = useState<RechnungCreateMode>("standard");
  const [abschlagProzent, setAbschlagProzent] = useState("50");
  const [abschlagFestbetrag, setAbschlagFestbetrag] = useState("");
  const [kunde, setKunde] = useState<AutocompleteOption<Geschaeftspartner> | null>(null);
  const [kundeQuelle, setKundeQuelle] = useState<GeschaeftspartnerQuelle | null>(null);
  const [kundeLoading, setKundeLoading] = useState(false);
  const [kundeError, setKundeError] = useState<string | null>(null);

  const selectedAuftragId = project?.value?.id ?? null;
  const primaryExistingDraft = existingDrafts[0] ?? null;
  const canConfigureDocument = selectedAuftragId != null;
  const isAbschlagMode =
    typ === "rechnung" &&
    (rechnungMode === "abschlag_prozent" || rechnungMode === "abschlag_festbetrag");
  const isSchlussrechnungMode = typ === "rechnung" && rechnungMode === "schlussrechnung";
  const needsQuellForRechnungMode = isAbschlagMode || isSchlussrechnungMode;
  const showGebuchteStundenOption =
    canConfigureDocument &&
    gebuchteStundenTotal != null &&
    gebuchteStundenTotal > 0 &&
    ((typ === "angebot" && inhaltMode !== "dokument") || isSchlussrechnungMode);
  const sourceTypen = CREATE_FROM_SOURCES[typ] ?? [];
  const showSourceSuggestions = canConfigureDocument && sourceTypen.length > 0;

  useEffect(() => {
    if (!open) return;
    setError(null);
    setLoading(false);
    setTyp(TYPEN.includes(initialTyp) ? initialTyp : "angebot");
    setInhaltMode(initialQuellDokumentId ? "dokument" : "leer");
    setSelectedSource(null);
    setSourcePickerOpen(false);
    setProject(null);
    setGebuchteStundenTotal(null);
    setIncludeGebuchteStunden(true);
    setDraftConflictOpen(false);
    setExistingDrafts([]);
    setLieferscheinConflictOpen(false);
    setExistingLieferscheine([]);
    setPendingLieferscheinSource(null);
    setSourceDocs([]);
    setConvertingFromId(null);
    setRechnungMode("standard");
    setAbschlagProzent("50");
    setAbschlagFestbetrag("");
    setKunde(null);
    setKundeQuelle(null);
    setKundeLoading(false);
    setKundeError(null);

    let cancelled = false;

    if (initialQuellDokumentId && Number.isFinite(initialQuellDokumentId) && initialQuellDokumentId > 0) {
      void getDokument(initialQuellDokumentId)
        .then((doc) => {
          if (!cancelled) setSelectedSource(doc);
        })
        .catch(() => {
          if (!cancelled) {
            setError("Das vorausgewählte Quelldokument konnte nicht geladen werden.");
          }
        });
    }

    if (!initialAuftragId) {
      setProjectLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setProjectLoading(true);
    void (async () => {
      try {
        const order = await getOrder(initialAuftragId);
        if (!cancelled) setProject(auftragLookupToOption(orderToAuftragLookup(order)));
      } catch {
        if (!cancelled) {
          setError("Das vorausgewählte Projekt konnte nicht geladen werden.");
        }
      } finally {
        if (!cancelled) setProjectLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, initialAuftragId, initialTyp, initialQuellDokumentId]);

  useEffect(() => {
    const needsHours =
      typ === "angebot" || (typ === "rechnung" && rechnungMode === "schlussrechnung");
    if (!open || !selectedAuftragId || !needsHours) {
      setGebuchteStundenTotal(null);
      setGebuchteStundenLoading(false);
      return;
    }
    let cancelled = false;
    setGebuchteStundenLoading(true);
    void fetchAuftragBookedHoursDetail(selectedAuftragId)
      .then((detail) => {
        if (!cancelled) setGebuchteStundenTotal(detail.totalHours);
      })
      .catch(() => {
        if (!cancelled) setGebuchteStundenTotal(null);
      })
      .finally(() => {
        if (!cancelled) setGebuchteStundenLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectedAuftragId, typ, rechnungMode]);

  useEffect(() => {
    if (typ !== "rechnung") {
      setRechnungMode("standard");
    }
  }, [typ]);

  useEffect(() => {
    if (needsQuellForRechnungMode && inhaltMode !== "dokument") {
      setInhaltMode("dokument");
    }
  }, [needsQuellForRechnungMode, inhaltMode]);

  useEffect(() => {
    if (!open || !selectedAuftragId || sourceTypen.length === 0) {
      setSourceDocs([]);
      setSourceDocsLoading(false);
      return;
    }
    let cancelled = false;
    setSourceDocsLoading(true);
    void (async () => {
      try {
        const batches = await Promise.all(
          sourceTypen.map((sourceTyp) =>
            listDokumente({
              auftrag_id: selectedAuftragId,
              typ: sourceTyp,
              limit: 20,
            }),
          ),
        );
        if (cancelled) return;
        const typRank = new Map(sourceTypen.map((t, i) => [t, i]));
        const merged = batches
          .flat()
          .filter(isConvertibleSource)
          .sort((a, b) => {
            const rankA = typRank.get(a.dokument_typ) ?? 99;
            const rankB = typRank.get(b.dokument_typ) ?? 99;
            if (rankA !== rankB) return rankA - rankB;
            const updatedA = a.updated_at ? Date.parse(a.updated_at) : 0;
            const updatedB = b.updated_at ? Date.parse(b.updated_at) : 0;
            return updatedB - updatedA;
          });
        setSourceDocs(merged);
      } catch {
        if (!cancelled) setSourceDocs([]);
      } finally {
        if (!cancelled) setSourceDocsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, selectedAuftragId, typ]);

  useEffect(() => {
    if (!open || typ !== "lieferschein" || !selectedAuftragId) {
      setExistingLieferscheine([]);
      setLieferscheinConflictOpen(false);
      setPendingLieferscheinSource(null);
      return;
    }
    let cancelled = false;
    void loadExistingLieferscheine(selectedAuftragId)
      .then((items) => {
        if (!cancelled) setExistingLieferscheine(items);
      })
      .catch(() => {
        if (!cancelled) setExistingLieferscheine([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectedAuftragId, typ]);

  const inhaltHint = useMemo(() => {
    if (!canConfigureDocument) {
      return "Bitte zuerst ein Projekt wählen.";
    }
    if (inhaltMode === "dokument") {
      return "Positionen werden aus einem bestehenden Dokument übernommen. Empfänger und Projektname kommen vom gewählten Projekt.";
    }
    return "Leeres Dokument – Stammdaten werden aus dem Projekt vorbelegt, Positionen können im Editor ergänzt werden.";
  }, [canConfigureDocument, inhaltMode]);

  async function handleKundeSelect(next: AutocompleteOption<Geschaeftspartner> | null) {
    setKunde(next);
    setKundeError(null);
    if (!next) {
      setKundeQuelle(null);
      return;
    }
    setKundeLoading(true);
    try {
      setKundeQuelle(await getGeschaeftspartnerQuelle(next.value.id));
    } catch (err) {
      setKundeQuelle(null);
      setKundeError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Kunde konnte nicht geladen werden.",
      );
    } finally {
      setKundeLoading(false);
    }
  }

  async function applySelectedKunde(created: DokumentDetail): Promise<DokumentDetail> {
    if (!kundeQuelle) return created;
    return updateDokument(created.id, {
      version: created.version,
      ...geschaeftspartnerQuelleToUpdate(kundeQuelle, typ),
    });
  }

  async function selectCreatedProject(order: CreatedOrder) {
    setError(null);
    setProjectLoading(true);
    try {
      const full = await getOrder(order.id);
      setProject(auftragLookupToOption(orderToAuftragLookup(full)));
    } catch {
      setProject(
        auftragLookupToOption({
          id: order.id,
          auftragsnummer: order.auftragsnummer ?? String(order.id),
          jahr: null,
          kunde: null,
          ort: null,
          ort_zusatz: null,
          beschreibung: null,
        }),
      );
    } finally {
      setProjectLoading(false);
    }
  }

  async function buildGebuchteStundenApiPosition(sortIndex: number, kundenId?: number | null) {
    if (!selectedAuftragId || !gebuchteStundenTotal || gebuchteStundenTotal <= 0) return null;
    let stundenArtikel: NachweisStundenArtikelDefaults | null = null;
    try {
      const raw = await getKundenStundenArtikel(kundenId ?? null, { auftragId: selectedAuftragId });
      if (raw.artikel_id != null || (raw.hero_nr || "").trim()) {
        stundenArtikel = {
          artikel_id: raw.artikel_id ?? null,
          hero_nr: raw.hero_nr ?? null,
          bezeichnung: raw.bezeichnung ?? null,
          einheit: raw.einheit ?? "Std.",
          einzelpreis_netto: raw.einzelpreis_netto ?? null,
          rabatt_prozent: raw.rabatt_prozent ?? null,
        };
      }
    } catch {
      stundenArtikel = null;
    }
    const editorPos = createGebuchteStundenPosition(sortIndex, gebuchteStundenTotal, stundenArtikel);
    return toApiPosition(editorPos, sortIndex, [editorPos]);
  }

  async function findExistingDrafts(auftragId: number, dokumentTyp: DokumentTyp) {
    const rows = await listDokumente({
      auftrag_id: auftragId,
      typ: dokumentTyp,
      status: "entwurf",
      limit: 20,
    });
    return rows.filter(
      (row) =>
        row.dokument_typ === dokumentTyp &&
        row.status === "entwurf" &&
        !isHeroCustomerDocumentListItem(row) &&
        Number.isFinite(row.id) &&
        row.id > 0,
    );
  }

  function parseAbschlagProzent(): number {
    const value = Number(String(abschlagProzent).replace(",", "."));
    if (!Number.isFinite(value) || value <= 0 || value > 100) {
      throw new Error("Bitte einen Abschlag zwischen 0 und 100 % eingeben.");
    }
    return value;
  }

  function parseAbschlagFestbetrag(): number {
    const value = Number(String(abschlagFestbetrag).replace(/\./g, "").replace(",", "."));
    if (!Number.isFinite(value) || value <= 0) {
      throw new Error("Bitte einen positiven Festbetrag (netto) eingeben.");
    }
    return value;
  }

  function buildRechnungCreateExtras(): Pick<
    DokumentCreateInput,
    | "abschlag_modus"
    | "abschlag_prozent"
    | "abschlag_betrag_netto"
    | "schlussrechnung_mit_abzuegen"
    | "rechnung_art"
  > {
    if (typ !== "rechnung" || rechnungMode === "standard") return {};
    if (rechnungMode === "abschlag_prozent") {
      return {
        abschlag_modus: "prozent",
        abschlag_prozent: parseAbschlagProzent(),
        rechnung_art: "teilrechnung",
      };
    }
    if (rechnungMode === "abschlag_festbetrag") {
      return {
        abschlag_modus: "festbetrag",
        abschlag_betrag_netto: parseAbschlagFestbetrag(),
        rechnung_art: "teilrechnung",
      };
    }
    return {
      schlussrechnung_mit_abzuegen: true,
      rechnung_art: "schlussrechnung",
    };
  }

  async function createRechnungFromSource(sourceId: number) {
    if (!selectedAuftragId) {
      throw new Error("Bitte zuerst ein Projekt auswählen.");
    }
    const extras = buildRechnungCreateExtras();
    const shouldAddStunden =
      isSchlussrechnungMode && showGebuchteStundenOption && includeGebuchteStunden;
    const positionen: DokumentPosition[] = [];
    if (shouldAddStunden) {
      const quelle = await getAuftragQuelle(selectedAuftragId);
      const stundenPos = await buildGebuchteStundenApiPosition(0, quelle.kunden_id);
      if (stundenPos) positionen.push(stundenPos);
    }
    const texts = await loadGdStandardtexteMap("rechnung");
    const created = await createDokument({
      dokument_typ: "rechnung",
      auftrag_id: selectedAuftragId,
      quell_dokument_id: sourceId,
      ...extras,
      ...(texts.einleitung ? { einleitung: texts.einleitung } : {}),
      ...(texts.schlusstext ? { schlusstext: texts.schlusstext } : {}),
      ...(positionen.length ? { positionen } : {}),
    });
    return ensureDokumentStandardtexte(created);
  }

  function finishCreated(docId: number) {
    setDraftConflictOpen(false);
    setLieferscheinConflictOpen(false);
    setPendingLieferscheinSource(null);
    onCreated(docId);
  }

  async function handleCreateFromSource(
    source: DokumentListItem,
    options?: { forceNew?: boolean },
  ) {
    setError(null);
    setConvertingFromId(source.id);
    setLoading(true);
    try {
      if (typ === "lieferschein" && !options?.forceNew) {
        if (!selectedAuftragId) {
          throw new Error("Bitte zuerst ein Projekt auswählen.");
        }
        const existing = await loadExistingLieferscheine(selectedAuftragId);
        if (existing.length) {
          setExistingLieferscheine(existing);
          setPendingLieferscheinSource(source);
          setLieferscheinConflictOpen(true);
          return;
        }
      }

      const sourceId = await resolveSourceDokumentId(source);

      // Angebot → AB: spezieller Dialog mit Bestellnummer / Positionswahl
      if (typ === "auftragsbestaetigung" && source.dokument_typ === "angebot") {
        onClose();
        openAuftragsbestaetigungFromAngebot({ angebotId: sourceId });
        return;
      }

      // Gleicher Typ → Kopie (z. B. neues App-Angebot aus Hero-Angebot)
      if (typ === source.dokument_typ) {
        if (!selectedAuftragId) {
          throw new Error("Bitte zuerst ein Projekt auswählen.");
        }
        const created = await ensureDokumentStandardtexte(
          await createDokument({
            dokument_typ: typ,
            auftrag_id: selectedAuftragId,
            quell_dokument_id: sourceId,
          }),
        );
        finishCreated(created.id);
        return;
      }

      if (typ === "rechnung") {
        setSelectedSource(source);
        setInhaltMode("dokument");
        const created = await createRechnungFromSource(sourceId);
        finishCreated(created.id);
        return;
      }

      const created = await ensureDokumentStandardtexte(await convertDokument(sourceId, typ));
      finishCreated(created.id);
    } catch (err) {
      setLieferscheinConflictOpen(false);
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Umwandlung fehlgeschlagen.",
      );
    } finally {
      setLoading(false);
      setConvertingFromId(null);
    }
  }

  async function handleCreate(options?: { forceNew?: boolean }) {
    if (!selectedAuftragId) {
      setError("Bitte zuerst ein Projekt auswählen.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (DRAFT_CONFLICT_TYPEN.includes(typ) && !options?.forceNew) {
        const drafts = await findExistingDrafts(selectedAuftragId, typ);
        if (drafts.length > 0) {
          setExistingDrafts(drafts);
          setDraftConflictOpen(true);
          setLoading(false);
          return;
        }
      }

      if (typ === "lieferschein" && !options?.forceNew) {
        const existing = await loadExistingLieferscheine(selectedAuftragId);
        if (existing.length) {
          setExistingLieferscheine(existing);
          setPendingLieferscheinSource(null);
          setLieferscheinConflictOpen(true);
          setLoading(false);
          return;
        }
      }

      const basePayload: DokumentCreateInput = {
        dokument_typ: typ,
        auftrag_id: selectedAuftragId,
      };
      const shouldAddStunden = showGebuchteStundenOption && includeGebuchteStunden;

      if (inhaltMode === "dokument" || needsQuellForRechnungMode) {
        if (!selectedSource) {
          throw new Error("Bitte ein Quelldokument auswählen.");
        }
        const sourceId = await resolveSourceDokumentId(selectedSource);
        if (typ === "rechnung") {
          const created = await createRechnungFromSource(sourceId);
          finishCreated(created.id);
          return;
        }
        const created = await ensureDokumentStandardtexte(
          await createDokument({
            ...basePayload,
            quell_dokument_id: sourceId,
          }),
        );
        finishCreated(created.id);
        return;
      }

      const quelle = await getAuftragQuelle(selectedAuftragId);
      const positionen: DokumentPosition[] = [];
      if (shouldAddStunden) {
        const stundenPos = await buildGebuchteStundenApiPosition(0, quelle.kunden_id);
        if (stundenPos) positionen.push(stundenPos);
      }
      const created = await applySelectedKunde(
        await ensureDokumentStandardtexte(
          await createDokument({
            ...basePayload,
            kunden_id: kundeQuelle?.kunden_id ?? quelle.kunden_id ?? undefined,
            ansprechpartner_id: quelle.ansprechpartner_id ?? undefined,
            betreff: quelle.betreff ?? undefined,
            ...(positionen.length ? { positionen } : {}),
          }),
        ),
      );
      finishCreated(created.id);
    } catch (err) {
      setLieferscheinConflictOpen(false);
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Dokument konnte nicht erstellt werden.",
      );
    } finally {
      setLoading(false);
    }
  }

  function handleOpenExistingDraft() {
    if (!primaryExistingDraft) return;
    finishCreated(primaryExistingDraft.id);
  }

  const createDialogLocked =
    loading || draftConflictOpen || lieferscheinConflictOpen || sourcePickerOpen;

  const typLabel = DOKUMENT_TYP_LABELS[typ];
  const draftConflictTitle =
    typ === "rechnung" ? "Rechnungsentwurf vorhanden" : "Angebotsentwurf vorhanden";
  const selectedSourceBetreff = selectedSource
    ? stripGdRichTextMarkup((selectedSource.betreff || "").trim())
    : "";

  function selectInhaltMode(mode: InhaltMode) {
    setInhaltMode(mode);
    if (mode === "dokument" && !selectedSource) {
      setSourcePickerOpen(true);
    }
  }

  return (
    <>
    <Dialog
      open={open}
      onClose={createDialogLocked ? () => undefined : onClose}
      dirty={project != null && project.value.id !== initialAuftragId}
      title="Neues Dokument"
      description="Zuerst Projekt wählen, dann Dokumenttyp und Inhalt festlegen."
      size="xl"
      fullScreenMobile
      zIndexClass={dialogZIndexClass}
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <ProjectOverviewButton auftragId={selectedAuftragId} />
          <div className="flex flex-wrap justify-end gap-2">
            <DialogCancelButton
              onClose={onClose}
              disabled={createDialogLocked}
            />
            <Button
              type="button"
              data-tour="dokument-erstellen"
              onClick={() => void handleCreate()}
              disabled={
                loading ||
                kundeLoading ||
                !canConfigureDocument ||
                draftConflictOpen ||
                lieferscheinConflictOpen ||
                sourcePickerOpen ||
                ((inhaltMode === "dokument" || needsQuellForRechnungMode) && !selectedSource) ||
                (rechnungMode === "abschlag_festbetrag" && !String(abschlagFestbetrag).trim())
              }
            >
              {loading && convertingFromId == null ? (
                <>
                  <Spinner className="mr-2" />
                  Wird erstellt …
                </>
              ) : (
                "Dokument erstellen"
              )}
            </Button>
          </div>
        </div>
      }
    >
      {error ? <Alert variant="error">{error}</Alert> : null}

      <div className="space-y-8">
        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-label">1. Projekt</h2>
            <NewProjectButton
              variant="secondary"
              size="sm"
              disabled={loading || projectLoading}
              onCreated={(order) => void selectCreatedProject(order)}
            >
              Neues Projekt
            </NewProjectButton>
          </div>
          {projectLoading ? (
            <div className="flex items-center gap-2 text-sm text-label-secondary">
              <Spinner />
              Projekt wird geladen …
            </div>
          ) : (
            <div data-tour="dokument-projekt">
              <LockedAutocomplete<AuftragLookup>
                label="Projekt"
                required
                placeholder={AUFTRAG_AUTOCOMPLETE_PLACEHOLDER}
                emptyText={AUFTRAG_AUTOCOMPLETE_EMPTY}
                value={project}
                onChange={setProject}
                loadOptions={loadAuftragAutocompleteOptions}
                hint="Jedes Dokument muss einem Projekt zugeordnet sein."
              />
            </div>
          )}
          {project ? (
            <div className="flex flex-wrap items-center gap-2 text-sm text-label-secondary">
              <span>
                Gewählt: <span className="font-medium text-label">{project.label}</span>
                {project.value.beschreibung ? ` · ${project.value.beschreibung}` : ""}
              </span>
              <ProjectOverviewButton auftragId={project.value.id} className="h-7 w-7 shrink-0" />
            </div>
          ) : null}
          {canConfigureDocument ? (
            <div className="space-y-2">
              <LockedAutocomplete<Geschaeftspartner>
                label="Kunde"
                placeholder="Kunden aus der App suchen…"
                emptyText="Kein Kunde gefunden"
                value={kunde}
                onChange={(next) => void handleKundeSelect(next)}
                minChars={0}
                disabled={loading || kundeLoading}
                loadOptions={loadAppKundeOptions}
                hint="Optional. Übernimmt Empfängerdaten aus den Kontakten. Ohne Auswahl gilt der Kunde vom Projekt."
              />
              {kundeLoading ? (
                <div className="flex items-center gap-2 text-sm text-label-secondary">
                  <Spinner className="h-4 w-4" />
                  Kundendaten werden geladen …
                </div>
              ) : null}
              {kundeError ? <p className="text-sm text-status-red">{kundeError}</p> : null}
            </div>
          ) : null}
        </section>

        <section className={clsx("space-y-3", !canConfigureDocument && "opacity-50")}>
          <h2 className="text-sm font-semibold text-label">2. Dokumenttyp</h2>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {TYPEN.map((t) => (
              <button
                key={t}
                type="button"
                data-tour={`dokument-typ-${t}`}
                disabled={!canConfigureDocument || loading}
                onClick={() => setTyp(t)}
                className={clsx(
                  "rounded-xl border p-4 text-left transition",
                  typ === t
                    ? "border-accent bg-accent-soft"
                    : "border-separator bg-surface hover:border-accent/40",
                  (!canConfigureDocument || loading) && "cursor-not-allowed",
                )}
              >
                <p className="font-semibold text-label">{DOKUMENT_TYP_LABELS[t]}</p>
              </button>
            ))}
          </div>

          {typ === "lieferschein" && existingLieferscheine.length > 0 ? (
            <ExistingLieferscheinAlert items={existingLieferscheine} />
          ) : null}

          {showSourceSuggestions ? (
            <div className="rounded-xl border border-separator bg-surface px-3 py-3">
              {sourceDocsLoading ? (
                <div className="flex items-center gap-2 text-sm text-label-secondary">
                  <Spinner className="h-4 w-4" />
                  Vorhandene Dokumente werden geprüft …
                </div>
              ) : sourceDocs.length > 0 ? (
                <div className="space-y-3">
                  <Alert variant="info" title="Vorhandenes Dokument nutzen">
                    {sourceDocs.length === 1 && sourceDocs[0] ? (
                      <>
                        Für dieses Projekt ist bereits{" "}
                        <span className="font-medium">
                          {getDokumentTypLabel(sourceDocs[0])}{" "}
                          {dokumentDisplayNumber(sourceDocs[0])}
                        </span>{" "}
                        vorhanden. Möchten Sie hieraus den {typLabel} erstellen?
                      </>
                    ) : (
                      <>
                        Für dieses Projekt gibt es bereits {sourceDocs.length} passende
                        Vorgänger-Dokumente. Sie können den {typLabel} daraus erstellen.
                      </>
                    )}
                  </Alert>
                  <ul className="space-y-2">
                    {sourceDocs.map((source) => {
                      const busy = convertingFromId === source.id;
                      return (
                        <li
                          key={source.id}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-separator bg-fill-quaternary/40 px-3 py-2"
                        >
                          <div className="min-w-0 text-sm">
                            <p className="font-medium text-label">
                              {getDokumentTypLabel(source)} {dokumentDisplayNumber(source)}
                              {isHeroOriginDokument(source) ? (
                                <span className="ml-2 rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-medium text-label-secondary">
                                  Hero
                                </span>
                              ) : null}
                            </p>
                            <p className="text-label-secondary">
                              {DOKUMENT_STATUS_LABELS[source.status] || source.status}
                              {source.betreff
                                ? ` · ${String(source.betreff).replace(/<[^>]+>/g, "").trim()}`
                                : ""}
                            </p>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            disabled={loading || lieferscheinConflictOpen}
                            loading={busy}
                            onClick={() => void handleCreateFromSource(source)}
                          >
                            Hieraus {typLabel} erstellen
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-label-secondary">
                  Keine finalisierten Vorgänger-Dokumente für die Erstellung eines {typLabel}s
                  gefunden. Sie können das Dokument leer anlegen.
                </p>
              )}
            </div>
          ) : null}
        </section>

        {typ === "rechnung" && canConfigureDocument ? (
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-label">3. Rechnungsmodus</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {RECHNUNG_CREATE_MODES.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  disabled={loading}
                  onClick={() => setRechnungMode(mode.id)}
                  className={clsx(
                    "rounded-xl border p-3 text-left transition",
                    rechnungMode === mode.id
                      ? "border-accent bg-accent-soft"
                      : "border-separator bg-surface hover:border-accent/40",
                    loading && "cursor-not-allowed",
                  )}
                >
                  <p className="font-semibold text-label">{mode.label}</p>
                  <p className="mt-1 text-xs text-label-secondary">{mode.hint}</p>
                </button>
              ))}
            </div>
            {rechnungMode === "abschlag_prozent" ? (
              <TextInput
                label="Abschlag in Prozent"
                type="text"
                inputMode="decimal"
                value={abschlagProzent}
                onChange={(e) => setAbschlagProzent(e.target.value)}
                disabled={loading}
                hint="Beispiel: 50 für die Hälfte der Angebotspositionen inkl. Projektnachlass."
              />
            ) : null}
            {rechnungMode === "abschlag_festbetrag" ? (
              <TextInput
                label="Festbetrag netto (€)"
                type="text"
                inputMode="decimal"
                value={abschlagFestbetrag}
                onChange={(e) => setAbschlagFestbetrag(e.target.value)}
                disabled={loading}
                hint="Wird anteilig auf die Angebotspositionen verteilt."
              />
            ) : null}
          </section>
        ) : null}

        <section className={clsx("space-y-3", !canConfigureDocument && "opacity-50")}>
          <h2 className="text-sm font-semibold text-label">
            {typ === "rechnung" ? "4. Inhalt" : "3. Inhalt"}
          </h2>
          <div className="flex flex-wrap gap-2">
            {(
              [
                ["leer", "Leer"],
                ["dokument", "Aus Dokument kopieren"],
              ] as const
            ).map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                disabled={
                  !canConfigureDocument ||
                  loading ||
                  (needsQuellForRechnungMode && mode === "leer")
                }
                onClick={() => selectInhaltMode(mode)}
                className={clsx(
                  "rounded-full px-3 py-1.5 text-sm font-medium",
                  inhaltMode === mode
                    ? "bg-accent text-white"
                    : "bg-fill-tertiary text-label-secondary hover:text-label",
                  (!canConfigureDocument || loading || (needsQuellForRechnungMode && mode === "leer")) &&
                    "cursor-not-allowed",
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-sm text-label-secondary">{inhaltHint}</p>

          {inhaltMode === "dokument" && canConfigureDocument ? (
            <div className="space-y-3 rounded-xl border border-separator bg-surface px-3 py-3">
              {selectedSource ? (
                <div className="min-w-0 text-sm">
                  <p className="font-medium text-label">
                    {getDokumentTypLabel(selectedSource)}{" "}
                    {dokumentDisplayNumber(selectedSource)}
                    {isHeroOriginDokument(selectedSource) ? (
                      <span className="ml-2 rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-medium text-label-secondary">
                        Hero
                      </span>
                    ) : null}
                  </p>
                  <p className="text-label-secondary">
                    {DOKUMENT_STATUS_LABELS[selectedSource.status] || selectedSource.status}
                    {selectedSource.kunde_name ? ` · ${selectedSource.kunde_name}` : ""}
                    {selectedSource.auftrag_nummer
                      ? ` · Projekt ${selectedSource.auftrag_nummer}`
                      : ""}
                    {selectedSourceBetreff ? ` · ${selectedSourceBetreff}` : ""}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-label-secondary">
                  Noch kein Quelldokument gewählt.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant={selectedSource ? "secondary" : "primary"}
                  size="sm"
                  disabled={loading}
                  onClick={() => setSourcePickerOpen(true)}
                >
                  {selectedSource ? "Dokument ändern…" : "Dokument wählen…"}
                </Button>
                {selectedSource ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={loading}
                    onClick={() => setSelectedSource(null)}
                  >
                    Auswahl löschen
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}

          {(typ === "angebot" && canConfigureDocument && inhaltMode !== "dokument") ||
          isSchlussrechnungMode ? (
            <div className="rounded-xl border border-separator bg-surface px-3 py-3">
              {gebuchteStundenLoading ? (
                <div className="flex items-center gap-2 text-sm text-label-secondary">
                  <Spinner className="h-4 w-4" />
                  Gebuchte Stunden werden geprüft …
                </div>
              ) : showGebuchteStundenOption ? (
                <Checkbox
                  label={
                    isSchlussrechnungMode
                      ? `Gebuchte Stunden (${formatHours(gebuchteStundenTotal)}) in die Schlussrechnung übernehmen`
                      : `Bereits geleistete Stunden (${formatHours(gebuchteStundenTotal)}) als angefallene Kosten übernehmen`
                  }
                  checked={includeGebuchteStunden}
                  onChange={(e) => setIncludeGebuchteStunden(e.target.checked)}
                  disabled={loading}
                />
              ) : (
                <p className="text-sm text-label-secondary">
                  Für dieses Projekt sind noch keine gebuchten Stunden vorhanden.
                </p>
              )}
            </div>
          ) : null}
        </section>
      </div>
    </Dialog>

    <Dialog
      open={draftConflictOpen}
      onClose={loading ? () => undefined : () => setDraftConflictOpen(false)}
      title={draftConflictTitle}
      description={
        existingDrafts.length > 1
          ? `Für dieses Projekt gibt es bereits ${existingDrafts.length} ${typLabel}-Entwürfe.`
          : `Für dieses Projekt gibt es bereits einen ${typLabel}-Entwurf.`
      }
      size="md"
      zIndexClass={conflictZIndexClass}
      plainBackdrop
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="secondary"
            disabled={loading}
            onClick={() => setDraftConflictOpen(false)}
          >
            Abbrechen
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={loading}
            onClick={() => void handleCreate({ forceNew: true })}
          >
            {loading ? (
              <>
                <Spinner className="mr-2" />
                Wird erstellt …
              </>
            ) : (
              "Neuen Entwurf erstellen"
            )}
          </Button>
          <Button
            type="button"
            disabled={loading || !primaryExistingDraft}
            onClick={handleOpenExistingDraft}
          >
            Zu vorhandenem Entwurf gehen
          </Button>
        </div>
      }
    >
      {primaryExistingDraft ? (
        <div className="space-y-3">
          <Alert variant="warning">
            Möchten Sie den vorhandenen Entwurf öffnen oder trotzdem einen neuen anlegen?
          </Alert>
          <div className="rounded-xl border border-separator bg-fill-quaternary/40 px-3 py-3 text-sm">
            <p className="font-medium text-label">
              {dokumentDisplayTitle(primaryExistingDraft)}
            </p>
            <p className="mt-1 text-label-secondary">
              {getDokumentTypLabel(primaryExistingDraft)} · Entwurf
              {primaryExistingDraft.updated_at
                ? ` · zuletzt ${new Date(primaryExistingDraft.updated_at).toLocaleString("de-DE", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}`
                : ""}
            </p>
            {existingDrafts.length > 1 ? (
              <p className="mt-2 text-xs text-label-secondary">
                Es wird der zuletzt geänderte Entwurf geöffnet. Weitere Entwürfe bleiben im
                Projekt erhalten.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </Dialog>

    <ExistingLieferscheinDialog
      open={lieferscheinConflictOpen}
      busy={loading}
      items={existingLieferscheine}
      zIndexClass={conflictZIndexClass}
      onClose={() => {
        if (loading) return;
        setLieferscheinConflictOpen(false);
        setPendingLieferscheinSource(null);
      }}
      onContinue={() => {
        const source = pendingLieferscheinSource;
        if (source) {
          void handleCreateFromSource(source, { forceNew: true });
          return;
        }
        void handleCreate({ forceNew: true });
      }}
      onOpenExisting={(id) => finishCreated(id)}
    />

    <DocumentSourcePickerDialog
      open={sourcePickerOpen}
      onClose={() => setSourcePickerOpen(false)}
      onSelect={(doc) => {
        setSelectedSource(doc);
        setInhaltMode("dokument");
        setError(null);
      }}
      auftragId={selectedAuftragId}
      selectedDoc={selectedSource}
      zIndexClass={conflictZIndexClass}
    />
    </>
  );
}
